import * as React from 'react'
import { MessageSquare, Send, ArrowDownLeft } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import type { LeadMessage } from './use-lead-detail'
import { parseApiDate } from '@/lib/dates'

const MESSAGE_TYPE_LABELS: Record<LeadMessage['message_type'], string> = {
  connection_request: 'Connection Request',
  dm: 'Direct Message',
  follow_up: 'Follow-Up',
  reply: 'Reply',
}

const TRUNCATE_LIMIT = 150

type MessageBubbleProps = {
  readonly message: LeadMessage
}

function MessageBubble({ message }: MessageBubbleProps) {
  const [expanded, setExpanded] = React.useState(false)
  const isSent = message.direction === 'sent'
  const needsTruncation = message.text.length > TRUNCATE_LIMIT
  const displayText =
    needsTruncation && !expanded ? `${message.text.slice(0, TRUNCATE_LIMIT)}…` : message.text

  return (
    <div className="flex gap-3 items-start">
      {/* Direction icon */}
      <div
        className={cn(
          'mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
          isSent ? 'bg-primary/10' : 'bg-muted',
        )}
      >
        {isSent ? (
          <Send className="h-3 w-3 text-primary" />
        ) : (
          <ArrowDownLeft className="h-3 w-3 text-muted-foreground" />
        )}
      </div>

      {/* Bubble */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] font-medium text-muted-foreground">
            {MESSAGE_TYPE_LABELS[message.message_type]}
          </span>
          <span className="text-[10px] text-muted-foreground/60">
            {parseApiDate(message.sent_at)?.toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            }) ?? '-'}
          </span>
        </div>
        <div
          className={cn(
            'rounded-lg px-3 py-2 text-xs leading-relaxed',
            isSent
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-foreground',
          )}
        >
          <p>{displayText}</p>
          {needsTruncation && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className={cn(
                'mt-1 text-[10px] underline underline-offset-2',
                isSent ? 'text-primary-foreground/70' : 'text-muted-foreground',
              )}
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

type ConversationHistoryProps = {
  readonly messages: readonly LeadMessage[]
  readonly isLoading?: boolean
}

export function ConversationHistory({ messages, isLoading = false }: ConversationHistoryProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3 items-start">
            <Skeleton className="h-6 w-6 rounded-full shrink-0" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-12 w-full rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (messages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center space-y-2">
        <div className="rounded-full bg-muted p-3">
          <MessageSquare className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">No messages yet</p>
        <p className="text-xs text-muted-foreground/60">
          Messages will appear here once outreach begins.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {messages.map((message) => (
        <MessageBubble key={message.id} message={message} />
      ))}
    </div>
  )
}
